import type { BlogPost } from "./types";

// Image guidance from source doc (photo omitted for now; add to public/blog/ later):
//  *   Smartphone showing accessible banking app with labelled buttons and high contrast — alt: "Smartphone displaying a mobile banking app with accessible button labels, high contrast colour scheme, and a screen reader announcing balance information"
//  *   Side-by-side comparison of inaccessible and accessible banking login form — alt: "Side-by-side comparison of an inaccessible bank login form with missing labels versus an accessible form with clearly labelled fields, error messages, and sufficient contrast"
const post: BlogPost = {
  slug: "bfsi-digital-accessibility-risk-india",
  title: "Accessibility Risk for BFSI Companies in India",
  metaTitle: "BFSI Accessibility Risk in India",
  metaDescription:
    "Banks, NBFCs and fintechs are among the most legally exposed for inaccessible apps. What accessibility risk looks like in BFSI and how to address it.",
  subtitle:
    "Banks, NBFCs and fintechs are among the most exposed to accessibility claims. What the risk looks like in BFSI and where to begin.",
  date: "2026-07-18",
  photo: "/blog/bfsi-digital-accessibility-risk-india.svg",
  alt:
    "Smartphone displaying a mobile banking app with accessible button labels, high contrast colour scheme, and a screen reader announcing balance information",
  author: "AccessBharat",
  tag: "BFSI",
  excerpt:
    "Banks, NBFCs and fintechs are among the most exposed to accessibility claims. What the risk looks like in BFSI and where to begin.",
  body: `Banking, insurance, mutual funds and payments have all moved to digital-first service over the last decade. Mobile banking apps, investment platforms, insurance portals and UPI flows are now how most customers deal with their financial institution. In that shift, customers with disabilities were often left behind.

## Regulatory exposure

BFSI companies answer to several regulators, including RBI, SEBI, IRDAI and PFRDA, and each has issued customer service guidelines that cover persons with disabilities. RBI's Master Circulars on Customer Service in Banks address accessible banking facilities directly. The RPwD Act 2016, including its Section 46 provisions on ICT, applies to any institution that provides financial services to the public.

Abroad, the exposure is sharper. US banks and financial firms are among the most frequently sued under the ADA over inaccessible websites and apps, and the European Accessibility Act names banking and financial services as a covered sector.

## Where the failures are

Common failures across Indian banking apps include:

- images with no alternative text
- low colour contrast
- form fields without proper labels
- OTP screens that time out before a screen reader user can finish
- CAPTCHAs that cannot be solved without sight
- screens that cannot be used with a keyboard alone

For a blind customer updating KYC, or a customer with a motor impairment transferring funds, these are not small annoyances. They block access to essential financial services.

## The customers you are missing

More than a billion people worldwide live with a disability. India's 2011 Census counted 26.8 million, widely seen as an undercount, and many more people benefit from accessible design, including older customers. Banks and NBFCs that make their digital channels accessible will serve people their competitors currently turn away, and the same changes usually make the product easier for everyone. Excluding these customers is also a legal liability.

## Where to start

Begin with the journeys that matter most: login and authentication, account summary, fund transfer and document download. A WCAG 2.1 Level AA audit of those journeys, on both web and mobile, will show what needs fixing. Add an Accessibility Conformance Report (ACR/VPAT) so you have a clear record of where you stand.
`,
};

export default post;
